import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import LookInside from "@/components/eden/LookInside";
import BookFireflies from "@/components/eden/BookFireflies";
import BookButterfly from "@/components/eden/BookButterfly";

const lineVariants = {
    hidden: { y: "115%" },
    show: (i) => ({
        y: "0%",
        transition: { duration: 1.15, ease: [0.22, 1, 0.36, 1], delay: 0.3 + i * 0.24 },
    }),
};

const MaskedLine = ({ index, className, children }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className={`block ${className}`}
            custom={index}
            variants={lineVariants}
            initial="hidden"
            animate="show"
        >
            {children}
        </motion.span>
    </span>
);

export default function HeroSection() {
    const sectionRef = useRef(null);
    const [lookOpen, setLookOpen] = useState(false);
    const mx = useMotionValue(0.5);
    const my = useMotionValue(0.5);
    const rotateX = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 50, damping: 14 });
    const rotateY = useSpring(useTransform(mx, [0, 1], [-9, 9]), { stiffness: 50, damping: 14 });

    const handleMouse = (e) => {
        const rect = sectionRef.current?.getBoundingClientRect();
        if (!rect) return;
        mx.set((e.clientX - rect.left) / rect.width);
        my.set((e.clientY - rect.top) / rect.height);
    };

    const scrollTo = (id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <section
            ref={sectionRef}
            onMouseMove={handleMouse}
            data-testid="hero-section"
            className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-6 pb-48 pt-28 text-center"
            style={{ perspective: "1200px" }}
        >
            <div className="garden-banner" data-testid="garden-banner">
                <video
                    className="garden-photo"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    poster="/assets/garden-river.jpg"
                    data-testid="garden-video"
                >
                    <source src="/assets/garden-river-mobile.mp4" type="video/mp4" media="(max-width: 768px)" />
                    <source src="/assets/garden-river.mp4" type="video/mp4" />
                    <source src="/assets/garden-river.webm" type="video/webm" />
                </video>
                <div className="garden-shade" />
            </div>
            <div className="hero-scrim" />
            <div className="god-light" />
            <div className="sky-beams" data-testid="sky-beams" />
            <div className="beam-halo" />
            <BookFireflies />

            <div className="relative z-10 flex flex-col items-center">
                <MaskedLine index={0} className="font-cinzel text-xs tracking-[0.4em] text-[#ffe9bd] uppercase [text-shadow:0_1px_4px_rgba(0,0,0,1),0_2px_20px_rgba(0,0,0,1),0_0_44px_rgba(0,0,0,0.95)] sm:text-sm">
                    What if we got the story wrong?
                </MaskedLine>

                <div className="mt-8 mb-10">
                    <MaskedLine index={1} className="font-cormorant text-lg italic leading-relaxed text-[#fdf3d0] [text-shadow:0_2px_18px_rgba(0,0,0,0.95)] sm:text-2xl">
                        Before Adam was a man, he was a child. Her child.
                    </MaskedLine>
                    <MaskedLine index={2} className="font-cormorant text-lg italic leading-relaxed text-[#fdf3d0] [text-shadow:0_2px_18px_rgba(0,0,0,0.95)] sm:text-2xl">
                        Before he knew strength, he knew his mother.
                    </MaskedLine>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 60, scale: 0.94 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.9 }}
                    className="book-float relative my-8 sm:ml-8"
                >
                    <BookButterfly />
                    <motion.img
                        src="/assets/eve-cover-book.png"
                        alt="Eve Came First — How Man Lost His Way in the Patriarchy by Victor M Medina"
                        data-testid="book-cover-image"
                        onClick={() => setLookOpen(true)}
                        className="book-glow-img block w-[62vw] max-w-[300px] cursor-pointer sm:max-w-[340px]"
                        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
                    />
                </motion.div>

                <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 2.4 }}
                    onClick={() => setLookOpen(true)}
                    data-testid="look-inside-cta"
                    className="btn-eden look-cta rounded-full bg-[rgba(15,35,24,0.8)] px-7 py-3 text-[0.7rem] text-[#f3e5ab] backdrop-blur-sm"
                >
                    Open the Book — Look Inside
                </motion.button>

                <h1 className="mt-8" data-testid="hero-title">
                    <MaskedLine index={3} className="font-cinzel text-4xl tracking-[0.06em] text-[#f0d9a8] sm:text-6xl lg:text-7xl [text-shadow:0_0_30px_rgba(255,200,100,0.35),0_0_70px_rgba(255,180,80,0.15),0_2px_20px_rgba(0,0,0,0.9)]">
                        EVE CAME FIRST
                    </MaskedLine>
                </h1>

                <div className="mt-6">
                    <MaskedLine index={4} className="font-cormorant text-base leading-loose tracking-[0.04em] text-[#c8b088] [text-shadow:0_2px_14px_rgba(0,0,0,0.85)] sm:text-xl">
                        A story of motherhood, loss,
                    </MaskedLine>
                    <MaskedLine index={5} className="font-cormorant text-base leading-loose tracking-[0.04em] text-[#c8b088] [text-shadow:0_2px_14px_rgba(0,0,0,0.85)] sm:text-xl">
                        and what it means to become a man.
                    </MaskedLine>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 2.1 }}
                    className="mt-12 flex flex-col items-center gap-4 sm:flex-row"
                >
                    <button
                        data-testid="hero-reserve-button"
                        onClick={() => scrollTo("preorder")}
                        className="btn-eden rounded-sm bg-gradient-to-br from-[rgba(160,120,60,0.9)] to-[rgba(120,85,40,0.9)] px-8 py-4 text-xs text-[#f5e6c8]"
                    >
                        Pre-Order — Coming Soon
                    </button>
                    <button
                        data-testid="hero-join-button"
                        onClick={() => scrollTo("remembering")}
                        className="btn-eden rounded-sm bg-[rgba(10,20,12,0.45)] px-8 py-4 text-xs text-[#d4af37] backdrop-blur-sm"
                    >
                        Join the Remembering
                    </button>
                </motion.div>
            </div>

            <LookInside open={lookOpen} onClose={() => setLookOpen(false)} />
        </section>
    );
}
